import { ShopApi } from '@/apis';
import RootStore from '.';
import BaseStore from './BaseStore';
import { flow, makeObservable, observable } from 'mobx';
import { ResponseData } from '@/types/http';
import { TableState } from '@tanstack/react-table';
import { Shop } from '@/types/shop';
import { CreateShopRequest, GetAllShopRequest, ShopServiceResponse, GetAllShopResponse, GetAllShopSearchSeedingResponse, GetShopInfoRequest, GetShopResponse, ProcessApproveRequest, UpdateAppointmentInfoRequest, UpdateGeneralInfoRequest, UpdateOperationInfoRequest, UpdateShopInfoRequest } from '@/types/http-payload/shop';

export default class ShopStore extends BaseStore {
    shops: Shop[] = [];
    totalByStatus: Object = {};
    api: ShopApi;
    shopInfo?: GetShopResponse;
    shopsSearchSeeding: any[] = [];
    listOptionShopService: ShopServiceResponse[] = [];
    shopServiceIdUpdate: string | any = '';

    constructor(rootStore: RootStore) {
        super(rootStore);
        makeObservable(this, {
            shopInfo: observable,
            shopsSearchSeeding: observable,
            shops: observable,
            listOptionShopService: observable,
            totalByStatus: observable,
            shopServiceIdUpdate: observable,
            getAllShop: flow.bound,
            getAllShopSearchSeeding: flow.bound,
            createShop: flow.bound,
            getShopInfo: flow.bound,
            getAllShopService: flow.bound,
            deleteShopService: flow.bound,
            createShopService: flow.bound,
            putShopService: flow.bound,
            updateGeneralInfo: flow.bound,
            updateShopInfo: flow.bound,
            updateOperationInfo: flow.bound,
            updateAppointmentInfo: flow.bound,
            updateStatus: flow.bound,
            deleteShop: flow.bound
        });
        this.api = new ShopApi();
    }

    *getAllShop(searchParams: GetAllShopRequest, paging?: TableState) {
        try {
            const payload = { ...this.convertPagingFromTableToRequest(paging ?? this.paging), ...searchParams };
            const res: ResponseData<GetAllShopResponse> = yield this.rootStore.apiStore.call(this.api, this.api.getAllShop, payload);
            if (res.ok) {
                this.shops = res.data.elements;
                this.totalByStatus = res.data.totalByStatus;
                this.paging = {
                    ...this.paging,
                    sorting: paging?.sorting ?? [],
                    pagination: this.convertPaginationFromRequestToTable(res.data.pagination)
                };
                this.totalRecord = res.data.pagination.totalRecord;
            }
        } catch (error) {
            console.log(error);
        }
    }

    *getAllShopSearchSeeding(searchParams: any) {
        try {
            const res: ResponseData<GetAllShopSearchSeedingResponse> = yield this.rootStore.apiStore.call(this.api, this.api.getAllShopSearchSeeding, searchParams, { hideLoading: true });
            if (res.ok) {
                this.shopsSearchSeeding = res.data.elements;
                return true;
            }
            return false;
        } catch (error) {
            console.log(error);
        }
    }

    * getAllShopService(code?: string, payload?: any) {
        try {
            const res: ResponseData<GetAllShopResponse> = yield this.rootStore.apiStore.call(this.api, this.api.getAllShopService, { code, payload }, { hideLoading: true });
            if (res.ok) {
                const shopServices = res.data.elements as any[];
                this.listOptionShopService = shopServices;
                return shopServices;
            }
        } catch (error) {
            console.log(error);
        }
    }

    * deleteShopService(code: string) {
        try {
            const res: ResponseData = yield this.rootStore.apiStore.call(this.api, this.api.deleteShopService, code);
            if (res.ok) {
                return res;
            }
        } catch (error) {
            console.log(error);
        }
    }

    * createShopService(code?: string, payload?: any) {
        try {
            const res: ResponseData = yield this.rootStore.apiStore.call(this.api, this.api.createShopService, { code, payload });
            if (res.ok) {
                return res;
            }
        } catch (error) {
            console.log(error);
        }
    }

    * putShopService(code?: any, payload?: any) {
        try {
            const res: ResponseData = yield this.rootStore.apiStore.call(this.api, this.api.putShopService, { code, payload });
            if (res.ok) {
                this.shopServiceIdUpdate = res.data;
                return res;
            }
        } catch (error) {
            console.log(error);
        }
    }

    * createShop(data: CreateShopRequest) {
        try {
            const res: ResponseData = yield this.rootStore.apiStore.call(this.api, this.api.createShop, data);
            if (res.ok) {
                return res;
            }
        } catch (error) {
            console.log(error);
        }
    }

    * updateStatus(code: string, payload: ProcessApproveRequest) {
        try {
            const res: ResponseData = yield this.rootStore.apiStore.call(this.api, this.api.updateStatus, { code, payload });
            if (res.ok) {
                return res;
            }
        } catch (error) {
            console.log(error);
        }
    }

    * getShopInfo(code: string, params?: GetShopInfoRequest) {
        try {
            const res: ResponseData<GetShopResponse> = yield this.rootStore.apiStore.call(this.api, this.api.getShopInfo, { code, params });
            if (res?.ok) {
                this.shopInfo = res?.data;
                return res;
            }
        } catch (error) {
        }
    }

    * updateGeneralInfo(code: string, payload: UpdateGeneralInfoRequest) {
        try {
            const res: ResponseData = yield this.rootStore.apiStore.call(this.api, this.api.updateGeneralInfo, { code, payload });
            if (res?.ok) {
                return true;
            }
        } catch (error) {
        }
    }

    * updateShopInfo(code: string, payload: UpdateShopInfoRequest) {
        try {
            const res: ResponseData = yield this.rootStore.apiStore.call(this.api, this.api.updateShopInfo, { code, payload });
            if (res?.ok) {
                return res;
            }
        } catch (error) {
        }
    }

    * updateOperationInfo(code: string, payload: UpdateOperationInfoRequest) {
        try {
            const res: ResponseData = yield this.rootStore.apiStore.call(this.api, this.api.updateOperationInfo, { code, payload });
            if (res?.ok) {
                return res;
            }
        } catch (error) {
        }
    }

    * updateAppointmentInfo(code: string, payload: UpdateAppointmentInfoRequest) {
        try {
            const res: ResponseData = yield this.rootStore.apiStore.call(this.api, this.api.updateAppointmentInfo, { code, payload });
            if (res?.ok) {
                return res;
            }
        } catch (error) {
        }
    }

    * deleteShop(code: string) {
        try {
            const res: ResponseData = yield this.rootStore.apiStore.call(this.api, this.api.deleteShop, code);
            if (res.ok) {
                return res;
            }
        } catch (error) {
            console.log(error);
        }
    }

    clean(): void {
        super.clean();
        this.shops = [];
    }
}
